// Projects (CR_Manager_vs_User_role §5.3; billing: UI design §3.7 — the CFO's month-end screen). A project has one
// or more locations; its page has the same tabs as a warehouse — Overview · Assets · Map · Stock counts · Movements —
// plus Billing for managers. A deployment opens when a transfer into a project location is received (dated from the
// arrival sighting) and closes when the asset is received somewhere else. On Billing a manager sees the days and
// amount for a period, corrects days or the rate with a reason (kept as an audit line) and exports it to Excel for
// invoicing. Everyone with the project in their access sees it; money is managers-only (RLS, and hidden here: P7).
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import type { CountStatus } from '@fleetspoke/core/counts';
import { placeKind } from '@fleetspoke/core/places';
import { tally } from '@fleetspoke/core/sites';
import { type FormEvent, lazy, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { canManage, useSession } from '../auth/session';
import { NewTransferButton } from '../components/NewTransferDialog';
import { OpenRequestsChip, RequestAssetsButton } from '../components/requests';
import {
  type ProjectApproval,
  useCompanyManagers,
  useProjectApprovals,
  useProjectApprovers,
  useSetProjectApproval,
  useRequestApprovalRequired,
  useSetProjectApprovers,
} from '../data/requests';
import { Empty, Loading, LoadError, Page } from '../components/Page';
import { PlaceAssets, ReceiveDialog } from '../components/place/PlaceAssets';
import { PlaceCounts } from '../components/place/PlaceCounts';
import {
  MovementsTable,
  ODD_LINE,
  PeriodPicker,
  useMovementViews,
} from '../components/place/PlaceMovements';
import { ProjectDetails } from '../components/place/ProjectDetails';
import { PlaceTallyFigures } from '../components/place/PlaceTally';
import { SiteCard } from '../components/place/SiteCard';
import { ZonesDialog } from '../components/ZonesDialog';
import {
  billingKeys,
  type BillingRow,
  type Project,
  PROJECT_STATUSES,
  type ProjectStatus,
  totals,
  useAdjustDays,
  useBilling,
  useProfileNames,
  useProject,
  useProjects,
  useSaveProject,
  useSetTerms,
} from '../data/billing';
import { keys, type SiteOverview, useSiteBoard, useSites } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import { useCountStatus, workflowKeys } from '../data/workflow';
import { activeZones, useZones } from '../data/zones';
import { withParam } from '../lib/assetFilters';
import {
  billingFileName,
  billingWorkbook,
  describeAdjustment,
  isoDateIn,
  type PeriodKind,
  periodInstants,
  periodRange,
  XLSX_TYPE,
} from '../lib/billing';
import { download } from '../lib/labels';
import { placePath, PROJECT_TABS, type ProjectTab } from '../lib/places';
import { supabase } from '../lib/supabase';
import { dateTime, isoMinuteIn, localeTag } from '../lib/time';
import { CountStateChip } from './CountsPage';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// MapLibre (+ Draw) load only when the Locations tab or a location dialog opens
const PlaceLocations = lazy(() =>
  import('../components/place/PlaceLocations').then((m) => ({ default: m.PlaceLocations })),
);
const SiteDialogLoader = lazy(() =>
  import('../components/SiteDialog').then((m) => ({ default: m.SiteDialogLoader })),
);

const STATUS_COLOR = {
  planned: 'info',
  active: 'success',
  completed: 'default',
  cancelled: 'default',
} as const;

// "2026-09-01 – …" (open-ended), or "—" when the project has no dates at all
const dateRange = (p: Pick<Project, 'start_date' | 'end_date'>) =>
  p.start_date || p.end_date ? `${p.start_date || '…'} – ${p.end_date || '…'}` : '—';

function money(v: number | null | undefined, language: string): string {
  if (v == null) return '—';
  return new Intl.NumberFormat(localeTag(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

/** A project's locations: its project / customer sites (a yard's project link does nothing, P9). */
const locationsOf = (sites: SiteOverview[] | undefined, projectId: string) =>
  (sites ?? [])
    .filter((s) => s.project_id === projectId && placeKind(s.type) === 'location')
    .sort((a, b) => a.name.localeCompare(b.name));

// the most pressing stock count state among a project's locations, for its card
const COUNT_URGENCY = [
  'overdue',
  'in_progress',
  'due',
  'done_late',
  'upcoming',
  'done',
  'not_scheduled',
];
const mostUrgent = (rows: CountStatus[]) =>
  [...rows].sort((a, b) => COUNT_URGENCY.indexOf(a.state) - COUNT_URGENCY.indexOf(b.state))[0];

/**
 * New / edit project: name, client, dates, status, note, and the managers asked to approve its requests
 * (CR_20260915_Request_Qty Q1; empty = every manager). Locations are added and linked inside the project (P6).
 */
function ProjectDialog({ project, onClose }: { project?: Project; onClose(id?: string): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const save = useSaveProject();
  const managers = useCompanyManagers();
  const currentApprovers = useProjectApprovers(project?.id ?? '');
  const setApprovers = useSetProjectApprovers();
  const [approvers, setApproverIds] = useState<string[] | null>(null);
  const currentApproval = useProjectApprovals(!!project?.id);
  const setProjectApproval = useSetProjectApproval();
  const [approvalEdit, setApproval] = useState<ProjectApproval | undefined>(undefined);
  const companyApproval = useRequestApprovalRequired(me?.company?.id);
  const approval =
    approvalEdit !== undefined
      ? approvalEdit
      : (currentApproval.data?.get(project?.id ?? '') ?? null);
  const approverIds = approvers ?? currentApprovers.data ?? [];
  const [name, setName] = useState(project?.name ?? '');
  const [client, setClient] = useState(project?.client_name ?? '');
  const [start, setStart] = useState(project?.start_date ?? '');
  const [end, setEnd] = useState(project?.end_date ?? '');
  const [status, setStatus] = useState<ProjectStatus>(project?.status ?? 'active');
  const [note, setNote] = useState(project?.note ?? '');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        id: project?.id,
        companyId: me!.company!.id,
        name,
        client_name: client,
        start_date: start,
        end_date: end,
        status,
        note,
      },
      {
        onSuccess: (id) => {
          if (!id) return onClose(id);
          // approval rule first, then the names: hiding the picker on 'none' must not silently drop a saved list
          if (approvalEdit !== undefined)
            setProjectApproval.mutate({ projectId: id, approval: approvalEdit });
          if (approvers !== null)
            return setApprovers.mutate(
              { projectId: id, userIds: approvers },
              { onSuccess: () => onClose(id) },
            );
          return onClose(id);
        },
      },
    );
  };
  const error = (save.error ?? setApprovers.error) as (Error & { code?: string }) | null;
  return (
    <Dialog open onClose={() => onClose()} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{project ? t('projects.edit') : t('projects.new')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={t('projects.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              label={t('projects.client')}
              value={client}
              onChange={(e) => setClient(e.target.value)}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                type="date"
                label={t('projects.start')}
                value={start}
                onChange={(e) => setStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                type="date"
                label={t('projects.end')}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                select
                label={t('projects.status')}
                value={status}
                onChange={(e) => setStatus(e.target.value as ProjectStatus)}
                sx={{ minWidth: 160 }}
              >
                {PROJECT_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`projects.status_${s}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label={`${t('projects.note')} (${t('common.optional')})`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
            />
            {/* D1: this project's own rule, or the company default */}
            <TextField
              select
              label={t('projects.approval')}
              value={approval ?? ''}
              onChange={(e) => setApproval((e.target.value || null) as ProjectApproval)}
              helperText={t('projects.approval_help')}
              // "" is the company default: shown, not an empty box
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              data-testid="project-approval"
            >
              <MenuItem value="">
                {companyApproval.data === undefined
                  ? t('projects.approval_default')
                  : t('projects.approval_default_is', {
                      value: t(
                        companyApproval.data
                          ? 'projects.approval_required'
                          : 'projects.approval_none',
                      ),
                    })}
              </MenuItem>
              <MenuItem value="required">{t('projects.approval_required')}</MenuItem>
              <MenuItem value="none">{t('projects.approval_none')}</MenuItem>
            </TextField>
            {/* Q2: a list that decides nothing is a false control, so it is hidden when nothing needs approving */}
            {approval === 'none' ? (
              <Typography variant="body2" color="text.secondary">
                {t('projects.approvers_hidden')}
              </Typography>
            ) : (
              <Autocomplete
                multiple
                options={(managers.data ?? []).map((m) => m.user_id)}
                value={approverIds}
                onChange={(_, v) => setApproverIds(v)}
                getOptionLabel={(id) => managers.data?.find((m) => m.user_id === id)?.name ?? '—'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t('projects.approvers')} (${t('common.optional')})`}
                    helperText={t('projects.approvers_help')}
                  />
                )}
                data-testid="project-approvers"
              />
            )}
            {error ? (
              <Alert severity="error">
                {error.code === '23505'
                  ? t('projects.name_taken')
                  : error.code === '23514'
                    ? t('projects.dates_invalid')
                    : error.message}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={save.isPending || !name.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** Links a location to a project (the link lives on the site: sites.project_id). */
function useLinkLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId, projectId }: { siteId: string; projectId: string }) => {
      const { data, error } = await supabase()
        .from('sites')
        .update({ project_id: projectId })
        .eq('id', siteId)
        .select('id');
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error('not linked');
    },
    onSuccess: () => {
      for (const key of [billingKeys.projects, keys.sites, ['site-rows']])
        void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * Link a location: from a project (pick one of the locations without a project) or from a location without a
 * project (pick the project).
 */
function LinkLocationDialog({
  projectId,
  siteId,
  onClose,
}: {
  projectId?: string;
  siteId?: string;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const sites = useSites();
  const projects = useProjects();
  const link = useLinkLocation();
  const unlinked = (sites.data ?? []).filter(
    (s) => s.is_active && placeKind(s.type) === 'location' && !s.project_id,
  );
  const [chosenSite, setChosenSite] = useState(siteId ?? '');
  const [chosenProject, setChosenProject] = useState(projectId ?? '');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {projectId ? t('projects.link_location') : t('projects.link_to_project')}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {projectId ? (
            unlinked.length ? (
              <TextField
                select
                label={t('places.kind_location')}
                value={chosenSite}
                onChange={(e) => setChosenSite(e.target.value)}
                helperText={t('projects.link_location_help')}
              >
                {unlinked.map((s) => (
                  <MenuItem key={s.site_id} value={s.site_id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography color="text.secondary">{t('projects.no_unlinked')}</Typography>
            )
          ) : (
            <TextField
              select
              label={t('projects.name')}
              value={chosenProject}
              onChange={(e) => setChosenProject(e.target.value)}
            >
              {(projects.data ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {link.error ? <Alert severity="error">{(link.error as Error).message}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!chosenSite || !chosenProject || link.isPending}
          onClick={() =>
            link.mutate({ siteId: chosenSite, projectId: chosenProject }, { onSuccess: onClose })
          }
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A project's card: who it is for and when, its locations' figures summed, its most pressing stock count. */
function ProjectCard({
  project: p,
  locations,
  counts,
  onOpen,
}: {
  project: Project;
  locations: SiteOverview[];
  counts: CountStatus[];
  onOpen(): void;
}) {
  const { t } = useTranslation();
  const active = locations.filter((s) => s.is_active);
  // sum the locations first, then read the buckets: an asset in location A's custody but sighted at sibling
  // location B is "elsewhere" for A, and summing per-site before clamping used to drop it from both
  const figures = tally(active);
  const count = mostUrgent(counts);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, height: '100%', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
      onClick={onOpen}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' ? onOpen() : undefined)}
      data-testid="project-card"
    >
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
        <Typography variant="h6" component="h2">
          {p.name}
        </Typography>
        <Chip
          size="small"
          color={STATUS_COLOR[p.status]}
          label={t(`projects.status_${p.status}`)}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {[p.client_name, dateRange(p), t('projects.n_locations', { count: active.length })]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
      <PlaceTallyFigures
        tally={figures}
        extra={count ? <CountStateChip s={count} standalone /> : null}
      />
    </Paper>
  );
}

type ProjectFilter = 'open' | 'closed' | 'all';
const FILTER_STATUSES: Record<ProjectFilter, ProjectStatus[]> = {
  open: ['active', 'planned'],
  closed: ['completed', 'cancelled'],
  all: [...PROJECT_STATUSES],
};

export function ProjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const manager = canManage(me);
  const projects = useProjects();
  const sites = useSites();
  const countStatus = useCountStatus();
  const [filter, setFilter] = useState<ProjectFilter>('open');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  useLiveInvalidate('projects', ['asset_presence', 'transfers', 'gateways'], [keys.sites]);
  const needle = search.trim().toLowerCase();
  const list = (projects.data ?? []).filter((p) => {
    if (!FILTER_STATUSES[filter].includes(p.status)) return false;
    if (!needle) return true;
    const names = [
      p.name,
      p.client_name ?? '',
      ...locationsOf(sites.data, p.id).map((s) => s.name),
    ];
    return names.some((n) => n.toLowerCase().includes(needle));
  });
  const unlinked = (sites.data ?? []).filter(
    (s) => s.is_active && placeKind(s.type) === 'location' && !s.project_id,
  );
  const countOf = (siteIds: string[]) =>
    (countStatus.data ?? []).filter((c) => siteIds.includes(c.site_id));

  return (
    <Page
      title={t('projects.title')}
      actions={
        manager ? (
          <Button variant="contained" onClick={() => setCreating(true)}>
            {t('projects.new')}
          </Button>
        ) : undefined
      }
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': t('common.search') },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, v: ProjectFilter | null) => v && setFilter(v)}
        >
          <ToggleButton value="open">{t('projects.filter_open')}</ToggleButton>
          <ToggleButton value="closed">{t('projects.filter_closed')}</ToggleButton>
          <ToggleButton value="all">{t('common.all')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {projects.isLoading ? <Loading /> : null}
      {projects.error ? <LoadError error={projects.error} /> : null}
      {projects.data && !list.length ? (
        <Empty>{projects.data.length ? t('places.no_match') : t('projects.none')}</Empty>
      ) : null}
      {list.length ? (
        <Grid container spacing={2}>
          {list.map((p) => {
            const locations = locationsOf(sites.data, p.id);
            return (
              <Grid key={p.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <ProjectCard
                  project={p}
                  locations={locations}
                  counts={countOf(locations.map((s) => s.site_id))}
                  onOpen={() => void navigate(`/projects/${p.id}`)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      {manager && unlinked.length ? (
        <Box component="section" sx={{ mt: 4 }} data-testid="unlinked-locations">
          <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
            {t('projects.unlinked_title')}{' '}
            <Typography component="span" color="text.secondary">
              ({unlinked.length})
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('sites.no_project')}
          </Typography>
          <Grid container spacing={2}>
            {unlinked.map((s) => (
              <Grid key={s.site_id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack spacing={1} sx={{ height: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <SiteCard
                      site={s}
                      count={countStatus.data?.find((c) => c.site_id === s.site_id)}
                      manager={manager}
                      onOpen={() => void navigate(placePath(s))}
                    />
                  </Box>
                  <Button size="small" onClick={() => setLinking(s.site_id)}>
                    {t('projects.link_to_project')}
                  </Button>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      {creating ? (
        <ProjectDialog
          onClose={(id) => {
            setCreating(false);
            if (id) void navigate(`/projects/${id}`);
          }}
        />
      ) : null}
      {linking ? <LinkLocationDialog siteId={linking} onClose={() => setLinking(null)} /> : null}
    </Page>
  );
}

function AdjustDialog({ row, onClose }: { row: BillingRow; onClose(): void }) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const adjust = useAdjustDays();
  const names = useProfileNames(row.adjustments.map((a) => a.by ?? '').filter(Boolean));
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');
  const n = Number(days);
  const valid = Number.isInteger(n) && n !== 0 && Math.abs(n) <= 366 && reason.trim().length >= 3;
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('billing.adjust_title')} · {row.asset_code}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography color="text.secondary">
            {t('billing.adjust_help', { days: row.days_billable })}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="number"
              label={t('billing.adjust_days')}
              helperText={t('billing.adjust_days_help')}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              slotProps={{ htmlInput: { step: 1, min: -366, max: 366 } }}
              sx={{ width: { sm: 180 } }}
              autoFocus
            />
            <TextField
              label={t('billing.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              required
            />
          </Stack>
          {row.adjustments.length ? (
            <Box>
              <Typography variant="subtitle2">{t('billing.history')}</Typography>
              <List dense data-testid="audit-lines">
                {row.adjustments.map((a, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemText
                      primary={`${describeAdjustment(a, t)} · ${a.reason ?? ''}`}
                      secondary={[
                        (a.by && names.data?.get(a.by)) || null,
                        a.at ? dateTime(a.at, i18n.language, tz) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null}
          {adjust.error ? <Alert severity="error">{(adjust.error as Error).message}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || adjust.isPending}
          onClick={() =>
            adjust.mutate(
              { deploymentId: row.deployment_id, days: n, reason: reason.trim() },
              { onSuccess: onClose },
            )
          }
        >
          {t('billing.adjust_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TermsDialog({ row, onClose }: { row: BillingRow; onClose(): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const setTerms = useSetTerms();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const [rate, setRate] = useState(row.rate == null ? '' : Number(row.rate).toFixed(2));
  const [expected, setExpected] = useState(
    row.expected_return_at ? isoDateIn(tz, new Date(row.expected_return_at)) : '',
  );
  const [reason, setReason] = useState('');
  const newRate = rate.trim() === '' ? null : Number(rate);
  const rateChanged = newRate !== (row.rate == null ? null : Number(row.rate));
  const valid =
    (newRate === null || (Number.isFinite(newRate) && newRate >= 0)) &&
    (!rateChanged || reason.trim().length >= 3);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t('billing.terms_title')} · {row.asset_code}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            type="number"
            label={`${t('billing.rate')}${row.currency ? ` (${row.currency})` : ''}`}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
            autoFocus
          />
          <TextField
            type="date"
            label={t('billing.expected_return')}
            value={expected}
            onChange={(e) => setExpected(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {rateChanged ? (
            <TextField
              label={t('billing.reason')}
              helperText={t('billing.rate_reason_help')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          ) : null}
          {setTerms.error ? (
            <Alert severity="error">{(setTerms.error as Error).message}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || setTerms.isPending}
          onClick={() =>
            setTerms.mutate(
              {
                deploymentId: row.deployment_id,
                rate: newRate,
                // a planning date: noon UTC is the same calendar day from UTC−11 to UTC+11
                expectedReturnAt: expected ? `${expected}T12:00:00Z` : null,
                reason: reason.trim(),
              },
              { onSuccess: onClose },
            )
          }
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Billing tab (managers): deployments in the period with days, rate and amount; adjust, terms, export. */
function ProjectBilling({
  project: p,
  range,
  rows,
  loading,
  error,
  onExport,
  exportError,
}: {
  project: Project;
  range: { from: string; to: string };
  rows: BillingRow[];
  loading: boolean;
  error: unknown;
  onExport(): void;
  exportError: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const [adjusting, setAdjusting] = useState<BillingRow | null>(null);
  const [terms, setTerms] = useState<BillingRow | null>(null);
  const sums = totals(rows);
  return (
    <Box data-testid="project-billing">
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button
          variant="contained"
          disabled={!rows.length}
          onClick={onExport}
          data-testid="export-xlsx"
          title={`${p.name} · ${range.from} – ${range.to}`}
        >
          {t('billing.export')}
        </Button>
      </Stack>
      {exportError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      ) : null}
      {loading ? <Loading /> : null}
      {error ? <LoadError error={error} /> : null}
      {!loading && !error && !rows.length ? <Empty>{t('billing.none')}</Empty> : null}
      {rows.length ? (
        <TableContainer>
          <Table size="small" data-testid="billing-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('billing.col_asset')}</TableCell>
                <TableCell>{t('assets.category')}</TableCell>
                <TableCell>{t('billing.col_site')}</TableCell>
                <TableCell>{t('billing.col_started')}</TableCell>
                <TableCell>{t('billing.expected_return')}</TableCell>
                <TableCell align="right">{t('billing.col_days')}</TableCell>
                <TableCell align="right">{t('billing.rate')}</TableCell>
                <TableCell align="right">{t('billing.col_amount')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.deployment_id} hover>
                  <TableCell>
                    <RouterLink to={`/assets/${r.asset_id}`}>{r.asset_code}</RouterLink>
                    {r.asset_description ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {r.asset_description}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{r.category_name ?? '—'}</TableCell>
                  <TableCell>{r.site_name ?? '—'}</TableCell>
                  <TableCell>
                    {dateTime(r.started_at, i18n.language, tz)}
                    {r.ended_at ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('billing.ended_at', { at: dateTime(r.ended_at, i18n.language, tz) })}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {r.expected_return_at ? isoDateIn(tz, new Date(r.expected_return_at)) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {r.days_billable}
                    {r.adjustment_days ? (
                      <Tooltip
                        title={r.adjustments
                          .filter((a) => a.days !== undefined)
                          .map((a) => `${describeAdjustment(a, t)} · ${a.reason ?? ''}`)
                          .join('\n')}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          label={`${r.adjustment_days > 0 ? '+' : '−'}${Math.abs(r.adjustment_days)}`}
                        />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    {r.rate == null ? (
                      <Chip size="small" color="warning" label={t('billing.no_rate')} />
                    ) : (
                      money(r.rate, i18n.language)
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {money(r.amount, i18n.language)} {r.currency ?? ''}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => setAdjusting(r)}>
                      {t('billing.adjust')}
                    </Button>
                    <Button size="small" onClick={() => setTerms(r)}>
                      {t('billing.terms')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              {sums.map((s) => (
                <TableRow key={s.currency} data-testid="billing-total">
                  <TableCell colSpan={5}>
                    <strong>{t('billing.total')}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{s.days}</strong>
                  </TableCell>
                  <TableCell />
                  <TableCell align="right">
                    <strong>
                      {money(s.amount, i18n.language)} {s.currency}
                    </strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
            </TableFooter>
          </Table>
        </TableContainer>
      ) : null}
      {adjusting ? <AdjustDialog row={adjusting} onClose={() => setAdjusting(null)} /> : null}
      {terms ? <TermsDialog row={terms} onClose={() => setTerms(null)} /> : null}
    </Box>
  );
}

/** One way to add a location, two choices (owner, 2026-09-15): a new one, or link one that exists. */
function AddLocationButton({ onNew, onLink }: { onNew(): void; onLink(): void }) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const pick = (fn: () => void) => () => {
    setAnchor(null);
    fn();
  };
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        data-testid="add-location"
      >
        {t('projects.add_location')}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        <MenuItem onClick={pick(onNew)}>{t('projects.new_location')}</MenuItem>
        <MenuItem onClick={pick(onLink)}>{t('projects.link_location')}</MenuItem>
      </Menu>
    </>
  );
}

export function ProjectDetailPage() {
  const { t } = useTranslation();
  const { projectId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { me } = useSession();
  const manager = canManage(me);
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const project = useProject(projectId);
  const sites = useSites();
  const countStatus = useCountStatus();
  const today = isoDateIn(tz);
  const [kind, setKind] = useState<PeriodKind>('this_month');
  const [custom, setCustom] = useState(() => periodRange('this_month', today));
  const range =
    kind === 'custom' ? custom : periodRange(kind, today, project.data?.start_date ?? null);
  const bounds = periodInstants(range, tz);
  const fromIso = bounds?.fromIso ?? '';
  const toIso = bounds?.toIso ?? '';
  const allLocations = useMemo(() => locationsOf(sites.data, projectId), [sites.data, projectId]);
  const locations = allLocations.filter((s) => s.is_active);
  const siteIds = useMemo(() => allLocations.map((s) => s.site_id), [allLocations]);

  // ?tab= (Billing only for managers, P7) and ?location= (the location the Assets tab and Receive act on)
  const wanted = params.get('tab');
  const tab: ProjectTab =
    (PROJECT_TABS as readonly string[]).includes(wanted ?? '') && (wanted !== 'billing' || manager)
      ? (wanted as ProjectTab)
      : 'overview';
  const location =
    allLocations.find((s) => s.site_id === params.get('location')) ??
    locations.find((s) => s.awaiting_receipt > 0) ??
    locations[0] ??
    allLocations[0];
  const board = useSiteBoard(location?.site_id ?? '');
  const zones = useZones(location?.site_id);
  const active = activeZones(zones.data);

  const billing = useBilling(projectId, range.from, range.to, manager && tab === 'billing');
  const rows = useMemo(() => billing.data ?? [], [billing.data]);
  const movement = useMovementViews(siteIds, fromIso, toIso);
  const { views, siteName, receiver } = movement;
  const names = useProfileNames(
    rows.flatMap((r) => r.adjustments.map((a) => a.by ?? '')).filter(Boolean),
  );
  // a receipt moves custody and so the deployments: refresh both when a transfer changes
  useLiveInvalidate(
    `project-${projectId}`,
    ['asset_presence', 'transfers', 'transfer_lines', 'gateways', 'asset_zone_moves'],
    [workflowKeys.transfers, ['billing'], keys.sites, ['board'], ['history'], ['zone-moves']],
  );
  const [editing, setEditing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [managingZones, setManagingZones] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [editingLocation, setEditingLocation] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);

  if (project.isLoading || sites.isLoading) return <Loading />;
  if (project.error || !project.data)
    return (
      <Page title={t('projects.title')}>
        <LoadError error={project.error ?? new Error(t('places.not_found'))} />
      </Page>
    );
  // an old ?tab=billing link for someone without money access
  if (wanted === 'billing' && !manager) return <Navigate to={`/projects/${projectId}`} replace />;
  const p = project.data;
  const setTab = (value: ProjectTab) =>
    setParams(withParam(params, 'tab', value === 'overview' ? '' : value), { replace: true });
  const setLocation = (id: string) =>
    setParams(withParam(params, 'location', id), { replace: true });
  const counts = (countStatus.data ?? []).filter((c) => siteIds.includes(c.site_id));
  // Receive acts on the chosen location only, so arrivals at the project's *other* locations need their own way in
  const elsewhere = locations.filter(
    (s) => s.site_id !== location?.site_id && s.awaiting_receipt > 0,
  );
  const elsewhereCount = elsewhere.reduce((n, s) => n + s.awaiting_receipt, 0);

  const movementSheet = () => ({
    sheet: t('movements.sheet'),
    header: [
      t('transfers.ref'),
      t('movements.direction'),
      t('transfers.from'),
      t('transfers.to'),
      t('transfers.assets'),
      t('transfers.status'),
      t('movements.dispatched'),
      t('movements.arrived'),
      t('movements.received'),
      t('movements.received_by'),
      t('movements.billing'),
    ],
    rows: views.map(({ m, direction, effect }) => [
      m.ref,
      t(`movements.dir_${direction}`),
      siteName(m.from_site_id, 'from'),
      siteName(m.to_site_id, 'to'),
      m.transfer_lines
        .map(
          (l) =>
            `${l.assets?.asset_code ?? ''}${ODD_LINE.includes(l.status) ? ` (${t(`transfers.line_${l.status}`)})` : ''}`,
        )
        .join(', '),
      t(`m.transfer.status.${m.status}`),
      isoMinuteIn(m.dispatched_at, tz) ?? '',
      isoMinuteIn(m.awaiting_since, tz) ?? '',
      isoMinuteIn(m.received_at, tz) ?? '',
      m.received_at ? receiver(m) : '',
      t(`movements.effect_${effect}`),
    ]),
  });

  async function exportXlsx() {
    setExporting(null);
    try {
      const bytes = await billingWorkbook({
        rows,
        projectName: p.name,
        clientName: p.client_name,
        from: range.from,
        to: range.to,
        timeZone: tz,
        names: names.data ?? new Map(),
        text: {
          title: t('billing.export_title'),
          project: t('projects.name'),
          client: t('projects.client'),
          period: t('billing.period'),
          generated: t('billing.generated'),
          sheet: t('billing.sheet'),
          auditSheet: t('billing.sheet_audit'),
          total: t('billing.total'),
          columns: {
            project: t('projects.name'),
            asset: t('billing.col_asset'),
            description: t('assets.description'),
            category: t('assets.category'),
            site: t('billing.col_site'),
            started: t('billing.col_started'),
            ended: t('billing.col_ended'),
            expected: t('billing.expected_return'),
            periodDays: t('billing.col_period_days'),
            adjustmentDays: t('billing.col_adjustment_days'),
            days: t('billing.col_days'),
            rate: t('billing.rate'),
            currency: t('billing.currency'),
            amount: t('billing.col_amount'),
            change: t('billing.col_change'),
            reason: t('billing.reason'),
            by: t('billing.col_by'),
            when: t('billing.col_when'),
          },
          describe: (a) => describeAdjustment(a, t),
        },
        movements: movementSheet(),
      });
      download(billingFileName(p.name, range.from, range.to), bytes, XLSX_TYPE);
    } catch (err) {
      setExporting(err instanceof Error ? err.message : String(err));
    }
  }

  const period = (
    <PeriodPicker
      kind={kind}
      onKind={setKind}
      custom={custom}
      onCustom={setCustom}
      range={range}
      kinds={['this_month', 'last_month', 'to_date', 'custom']}
    />
  );
  const shownLocations = showInactive ? allLocations : locations;
  const tabs = PROJECT_TABS.filter((v) => v !== 'billing' || manager);

  return (
    <Page
      back={
        <Link component={RouterLink} to="/projects" variant="body2">
          ← {t('nav.projects')}
        </Link>
      }
      title={
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{p.name}</span>
          <Chip
            size="small"
            color={STATUS_COLOR[p.status]}
            label={t(`projects.status_${p.status}`)}
          />
        </Stack>
      }
      actions={
        <>
          {manager ? (
            <Button variant="outlined" onClick={() => setEditing(true)}>
              {t('common.edit')}
            </Button>
          ) : null}
          {/* CR_20260915_Request_Qty: ask a warehouse for category × quantity (whoever works at a location) */}
          <RequestAssetsButton projectName={p.name} locations={locations} />
          {/* From = the project's location, or a choice among its locations (R0); "Transfers" (owner, 2026-09-15) */}
          <NewTransferButton fromIds={locations.map((s) => s.site_id)} />
          <Button
            variant="contained"
            disabled={!location || location.awaiting_receipt === 0}
            onClick={() => setReceiving(true)}
            data-testid="receive"
            title={location?.name}
          >
            {t('board.receive', { count: location?.awaiting_receipt ?? 0 })}
            {locations.length > 1 && location?.awaiting_receipt ? ` · ${location.name}` : ''}
          </Button>
        </>
      }
    >
      {/* client and dates are on the Overview's Project details; only what Receive can't show stays here:
          the arrivals waiting at another of the project's locations, as the way to get to them (owner, 2026-09-20) */}
      {elsewhereCount > 0 ? (
        <Link
          component="button"
          type="button"
          color="text.secondary"
          underline="hover"
          onClick={() =>
            elsewhere.length === 1 ? setLocation(elsewhere[0]!.site_id) : setTab('map')
          }
          sx={{ display: 'block', width: 'fit-content', mt: -1, mb: 2, textAlign: 'left' }}
          data-testid="project-summary"
        >
          {elsewhere.length === 1
            ? t('places.receive_at_place', {
                count: elsewhereCount,
                name: elsewhere[0]!.name,
              })
            : t('places.receive_at_others', {
                count: elsewhereCount,
                places: elsewhere.length,
              })}{' '}
          →
        </Link>
      ) : null}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v: ProjectTab) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {tabs.map((value) => (
            <Tab
              key={value}
              value={value}
              // the ?tab=map key stays; on a project the tab holds the locations list beside the map
              label={t(value === 'map' ? 'places.tab_locations' : `places.tab_${value}`)}
              data-testid={`tab-${value}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* the Overview is the summary card alone (owner, 2026-09-20): the tally bar, the gateway figure and the
          per-location stock counts that used to sit under it are on the Sites, Assets and Stock counts tabs */}
      {tab === 'overview' ? (
        <ProjectDetails
          project={p}
          locations={locations}
          onOpenSites={() => setTab('map')}
          onOpenAssets={() => setTab('assets')}
        />
      ) : null}

      {tab === 'assets' ? (
        location ? (
          <>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}
            >
              {locations.length > 1
                ? locations.map((s) => (
                    <Chip
                      key={s.site_id}
                      label={s.name}
                      color={s.site_id === location.site_id ? 'primary' : 'default'}
                      variant={s.site_id === location.site_id ? 'filled' : 'outlined'}
                      onClick={() => setLocation(s.site_id)}
                      data-testid="location-chip"
                    />
                  ))
                : null}
              <Box sx={{ flexGrow: 1 }} />
              {manager ? (
                <Button size="small" variant="outlined" onClick={() => setManagingZones(true)}>
                  {active.length ? `${t('zones.button')} (${active.length})` : t('zones.button')}
                </Button>
              ) : null}
            </Stack>
            <PlaceAssets key={location.site_id} site={location} />
          </>
        ) : (
          <Empty>{t('projects.no_sites')}</Empty>
        )
      ) : null}

      {tab === 'map' ? (
        <Suspense fallback={<Loading />}>
          <PlaceLocations
            sites={shownLocations}
            counts={counts}
            manager={manager}
            header={
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1.5 }}
                data-testid="project-locations"
              >
                <Typography variant="h6" component="h2">
                  {t('projects.locations')}{' '}
                  <Typography component="span" color="text.secondary">
                    ({locations.length})
                  </Typography>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {allLocations.length > locations.length ? (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={showInactive}
                          onChange={(e) => setShowInactive(e.target.checked)}
                        />
                      }
                      label={t('places.show_inactive')}
                    />
                  ) : null}
                  {manager ? (
                    <AddLocationButton
                      onNew={() => setAddingLocation(true)}
                      onLink={() => setLinking(true)}
                    />
                  ) : null}
                </Stack>
              </Stack>
            }
            onAssets={(s) =>
              setParams(withParam(withParam(params, 'location', s.site_id), 'tab', 'assets'), {
                replace: true,
              })
            }
            onEdit={(s) => setEditingLocation(s.site_id)}
          />
        </Suspense>
      ) : null}

      {tab === 'counts' ? <PlaceCounts siteIds={siteIds} /> : null}

      {tab === 'movements' ? (
        <Box data-testid="project-movements">
          {period}
          <MovementsTable
            {...movement}
            showBilling={manager}
            chips={<OpenRequestsChip projectId={p.id} />}
          />
        </Box>
      ) : null}

      {tab === 'billing' && manager ? (
        <>
          {period}
          <ProjectBilling
            project={p}
            range={range}
            rows={rows}
            loading={billing.isLoading}
            error={billing.error}
            onExport={() => void exportXlsx()}
            exportError={exporting}
          />
        </>
      ) : null}

      {editing ? <ProjectDialog project={p} onClose={() => setEditing(false)} /> : null}
      {receiving && location ? (
        <ReceiveDialog
          siteId={location.site_id}
          board={board.data ?? []}
          zones={active}
          open={receiving}
          onClose={() => setReceiving(false)}
        />
      ) : null}
      {managingZones && location && me?.company ? (
        <ZonesDialog
          siteId={location.site_id}
          siteName={location.name}
          companyId={location.company_id}
          onClose={() => setManagingZones(false)}
        />
      ) : null}
      {addingLocation || editingLocation ? (
        <Suspense fallback={<Loading />}>
          <SiteDialogLoader
            kind="location"
            siteId={editingLocation ?? undefined}
            projectId={projectId}
            onClose={() => {
              setAddingLocation(false);
              setEditingLocation(null);
            }}
          />
        </Suspense>
      ) : null}
      {linking ? (
        <LinkLocationDialog projectId={projectId} onClose={() => setLinking(false)} />
      ) : null}
    </Page>
  );
}
